import { useMemo } from "react";
import { useUpdateProjectMemberRole } from "@/app/members/api/hooks";
import type { ProjectMember } from "@/app/members/types";
import { useProjectRoles } from "@/app/roles/api/hooks";
import {
  RoleSwitcher,
  type RoleSwitcherOption,
} from "@/app/roles/components/role-switcher";
import { ROLE } from "@/app/roles/types";
import { useSession } from "@/lib/auth-client";

type MemberRoleSwitcherProps = {
  member: ProjectMember & { role: NonNullable<ProjectMember["role"]> };
};

export function MemberRoleSwitcher({ member }: MemberRoleSwitcherProps) {
  const { roles } = useProjectRoles();
  const { mutateAsync: changeRole, isPending } = useUpdateProjectMemberRole();
  const { data: session } = useSession();

  // Always include the member's current role so the trigger has a label even
  // before the catalog loads (or if the role isn't in the assignable set).
  const options = useMemo<RoleSwitcherOption[]>(() => {
    const list = roles.map((role) => ({
      key: role.key,
      label: role.display_name,
    }));
    if (!list.some((option) => option.key === member.role.key)) {
      list.unshift({ key: member.role.key, label: member.role.display_name });
    }
    return list;
  }, [roles, member.role]);

  return (
    <RoleSwitcher
      currentKey={member.role.key}
      dialogTitle="Change role"
      isPending={isPending}
      onConfirm={(role) => changeRole({ memberId: member.account_id, role })}
      options={options}
      selfDemotionKey={ROLE.projectMember}
      selfDemotionMessage="You'll lose admin access to this project and may not be able to change it back yourself."
      subject={{
        isCurrentUser: session?.user.accountId === member.account_id,
        email: member.email,
      }}
    />
  );
}
